if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('.country-filter__live-region'),
        };
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('input', this.onSearchInput.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        /*
          ADA #191 - The picker is a live filter inside a collapsible popup, not an ARIA
          combobox. The options are <button>s reached with Tab and activated with Enter
          or Space, so there is no arrow-key roving and no aria-activedescendant, and
          opening the popup leaves focus on the trigger.
        */
        this.querySelectorAll('.disclosure__link').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this))
        );
      }

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
        }
        if (this.elements.liveRegion) {
          clearTimeout(this.liveRegionTimeout);
          this.elements.liveRegion.textContent = '';
        }
        document.removeEventListener('click', this.onDocumentClick, true);
        document.body.classList.remove('overflow-hidden-mobile');
        const drawer = document.querySelector('.menu-drawer');
        if (drawer) drawer.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
        this.hidePanel();
        event.stopPropagation();
        this.elements.button.focus();
      }

      onCloseButtonClick() {
        this.hidePanel();
        this.elements.button.focus();
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        this.elements.button.focus();
        if (this.elements.button.getAttribute('aria-expanded') === 'true') {
          this.hidePanel();
          return;
        }
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick, true);
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.hasAttribute('data-prevent-hide') && this.header) {
          this.header.preventHide = true;
        }
        const drawer = document.querySelector('.menu-drawer');
        if (drawer) drawer.classList.add('country-selector-open');
      }

      /*
        Closes when focus moves to an element outside the picker. A focusout with no
        relatedTarget is not a reason to close: Safari does not focus a <button> on
        click, so clicking a country blurs the trigger towards nothing and the popup
        would be hidden before the click lands. Clicks outside are handled below.
      */
      closeSelector(event) {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.hidePanel();
        }
      }

      /*
        Runs in the capture phase so this picker is closed before the click reaches its
        target. hidePanel() releases state shared with other pickers (header.preventHide,
        the body scroll lock), so when the click opens another picker the order has to
        be "close this one, then open that one".
      */
      onDocumentClick(event) {
        if (this.elements.panel.contains(event.target) || this.elements.button.contains(event.target)) return;
        this.hidePanel();
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const items = this.querySelectorAll('.countries .disclosure__item');
        let visibleCountries = 0;

        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', Boolean(searchValue));
        }

        items.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          const matches = countryName.indexOf(searchValue) > -1;
          item.classList.toggle('hidden', !matches);
          if (matches) visibleCountries++;
        });

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;

        return visibleCountries;
      }

      /*
        Announces the result count and how to reach the results. The region is emptied
        first so the same count is announced again on the next keystroke, and emptied
        afterwards so the message cannot be reached with a screen reader's virtual cursor.
      */
      announceResults(count) {
        const liveRegion = this.elements.liveRegion;
        if (!liveRegion || !window.accessibilityStrings) return;

        clearTimeout(this.liveRegionTimeout);
        liveRegion.textContent = '';
        liveRegion.textContent = window.accessibilityStrings.countrySelectorSearchCount.replace('[count]', count);
        this.liveRegionTimeout = setTimeout(() => {
          liveRegion.textContent = '';
        }, 1000);
      }

      onSearchInput() {
        this.announceResults(this.filterCountries());
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.announceResults(this.filterCountries());
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
